// Angular imports
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';

// Import sanitize html
import sanitizeHtml from 'sanitize-html';

const ALLOWED_TAGS = [
    'div', 'b', 'em', 'u', 's', 'br', 'i'
    // 'div', 'span', 'p', 'b', 'em', 'u', 's', 'br', 'i'
]

@Component({
    selector: 'multi-line-text-input',
    templateUrl: './multi-line-text-input.component.html',
    styleUrls: ['./multi-line-text-input.component.scss'],
    standalone: false
})
export class MultiLineTextInputComponent implements OnInit {
    // The value of the input
    @Input() public value: string = '';
    @Output() public valueChange = new EventEmitter<string>();
    @Output() public input = new EventEmitter<string>();

    @Input() public placeholder: string = '';

    // Whether or not this input is readonly
    @Input() public readonly: boolean = false;

    // Event handlers
    @Output('focus') public onFocus = new EventEmitter<FocusEvent>();
    @Output('blur') public onBlur = new EventEmitter<void>();
    @Output('keydown') public onKeydown = new EventEmitter<KeyboardEvent>();

    // Reference to the input element
    @ViewChild('input', { static: true }) public _inputElement!: ElementRef<HTMLInputElement>;

    // The current text
    private _innerText?: string = undefined;
    private hasFocus: boolean = false;

    constructor() { }

    public ngOnInit() { }

    /**
     * Focuses the input's content
     * @returns void
     */
    public focus(): void {
        // Ensure that value and inner text are the same when focussing
        this._innerText = this.value;

        // Move selection to end
        const selection = window.getSelection();
        const range = document.createRange();
        selection?.removeAllRanges();
        range.selectNodeContents(this._inputElement.nativeElement);
        range.collapse(false);
        selection?.addRange(range);

        // Focus
        this._inputElement.nativeElement.focus();

        // Set has focus
        this.hasFocus = true;
    }

    /**
     * Blurs the input's content (if it has focus)
     * @returns void
     */
    public blur(): void {
        this._inputElement.nativeElement.blur();
    }

    /**
     * Selects the input's content
     * @returns void
     */
    public select(): void {
        this._inputElement.nativeElement.select();
    }

    /* --- Event handlers --- */

    public _handlePaste(event: ClipboardEvent): void {
        if (this.hasFocus) return;

        // Prevent default and stop propagation
        event.preventDefault();
        event.stopPropagation();

        // Get data
        const data = sanitizeHtml(
            (
                event.clipboardData?.getData('text/html')
                || event.clipboardData?.getData('text/plain')
            ) ?? '', {
            allowedTags: ALLOWED_TAGS, allowedAttributes: {}
        });

        // Create div
        const div = document.createElement('div');

        // Set inner html
        div.innerHTML = sanitizeHtml(data, { allowedTags: ALLOWED_TAGS, allowedAttributes: {} });

        // Ensure cell is selected
        this.focus();

        // Write data
        const selection = window.getSelection();
        if (!selection?.rangeCount) return;
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(div);

        // Update value
        this._innerText = this._inputElement.nativeElement.innerHTML;
        this.value = this._innerText;

        // Trigger change
        this.input.emit(this._innerText);
        this.valueChange.emit(this._innerText);
    }

    public _handleValueChange(event: Event) {
        // Set the inner text
        this._innerText = sanitizeHtml(
            (event.target as HTMLElement)?.innerHTML ?? '',
            {
                allowedTags: ALLOWED_TAGS,
                allowedAttributes: {},
                // 20260414 Fix for newlines in rich text input
                textFilter(text) {
                    return text.replace(/\r\n/g, '\n').replace(/\n+/g, ' ');
                }
            });

        // Input
        this.input.emit(this._innerText);
    }

    public _handleClick(event: MouseEvent) {

        // Prevent default
        if (!this.hasFocus)
            event.preventDefault();

        // Focus
        this.focus();

        // Trigger event
        this.onFocus.emit(event);
    }

    public _handleFocus(event: FocusEvent) {
        // Stop propagation
        event.preventDefault();
        event.stopPropagation();

        // Trigger event
        if (!this.hasFocus)
            this.onFocus.emit(event);

        // Set has focus
        this.hasFocus = true;
    }

    public _handleBlur() {
        // Unset has focus
        this.hasFocus = false;

        // If the value was changed, trigger event
        if (this._innerText !== this.value) {
            // Assign value
            this.value = this._innerText ?? '';

            // Trigger the event
            this.valueChange.emit(this.value);
        }

        // Trigger event
        this.onBlur.emit();
    }

    public _handleKeydown(event: KeyboardEvent) {
        // Pass event up
        this.onKeydown.emit(event);
    }
}
